/**
 * Resolves the force-landscape rotation direction (in degrees) for the
 * force-vertical player.
 *
 * Why this exists: when the display is orientation-locked to portrait (the
 * Android force-vertical fullscreen path), `screen.orientation.angle` is pinned
 * to 0 and can no longer tell us which way the user physically rotated the
 * phone. A fixed +90deg then looks upside-down for one of the two landscape
 * holds. The accelerometer's `gamma` (left-right tilt, [-90, 90]) is the one
 * runtime signal whose sign distinguishes the two holds, so we prefer it when
 * available and fall back to the legacy screen-angle heuristic otherwise.
 *
 * Direction convention (confirmed on-device): the landscape-primary hold reads
 * `gamma < 0` and must render at `+90` (a `-90` there was upside-down per the
 * Shea/Hunter reports). So `gamma < 0 => +90` and `gamma > 0 => -90`, matching
 * {@link mapLandscapeAngleToRotateDeg}'s screen-angle mapping.
 */

/**
 * Minimum absolute `gamma` before we trust it as a landscape hold. Below this
 * the phone is roughly flat or upright, where gamma is ambiguous/noisy, so we
 * keep the previous direction instead of flipping mid-rotation.
 */
export const GAMMA_LANDSCAPE_THRESHOLD_DEG = 20;

/**
 * Maps a real landscape `screen.orientation.angle` to the CSS rotation that
 * reproduces that landscape view inside a portrait-locked viewport.
 *
 * `landscape-primary` (angle 90) needs `+90`; `landscape-secondary` (angle 270)
 * needs `-90`. Confirmed on-device: the earlier gamma-derived `-90` for the
 * landscape-primary hold rendered the stream upside-down (Shea/Hunter report),
 * so the correct upright rotation for that hold is `+90` and the opposite hold
 * is the mirror.
 */
const mapLandscapeAngleToRotateDeg = (landscapeAngle: number): number =>
  landscapeAngle === 270 ? -90 : 90;

/**
 * @param angle                `screen.orientation.angle` (0/90/180/270; 0 when unsupported).
 * @param gamma                Latest `DeviceOrientationEvent.gamma`, or null when the
 *                             accelerometer is unavailable / not yet reporting.
 * @param previousDeg          Last committed rotation (used as hysteresis in the
 *                             ambiguous near-flat zone).
 * @param lockedLandscapeAngle The real landscape angle (90/270) captured just
 *                             before `screen.orientation.lock('portrait')` pinned
 *                             `angle` to 0, or null when we are not in the
 *                             fullscreen-locked state. When present it is
 *                             authoritative and gamma is ignored: Chrome
 *                             re-expresses `deviceorientation` in the locked
 *                             portrait frame, which makes gamma's sign oscillate
 *                             (the observed video flicker). The captured screen
 *                             angle is stable, so we hold it instead.
 * @returns 90 or -90.
 */
export const resolveForceRotateDeg = (
  angle: number,
  gamma: number | null,
  previousDeg: number,
  lockedLandscapeAngle: number | null = null,
): number => {
  if (lockedLandscapeAngle !== null) {
    return mapLandscapeAngleToRotateDeg(lockedLandscapeAngle);
  }
  if (gamma !== null) {
    if (Math.abs(gamma) >= GAMMA_LANDSCAPE_THRESHOLD_DEG) {
      // gamma < 0 => landscape-primary hold, which renders upright at +90.
      return gamma < 0 ? 90 : -90;
    }
    // Near-flat / upright: ambiguous. Honor an explicit upside-down portrait
    // (angle === 180) so we keep the pre-fix -90 for that hold; otherwise hold
    // the last decision so the image does not flip while the user is
    // mid-rotation or holding the phone flat.
    if (angle === 180) return -90;
    return previousDeg;
  }
  // No accelerometer (desktop, iOS without motion permission): legacy
  // screen-angle heuristic, unchanged so those platforms behave as before.
  return angle === 180 ? -90 : 90;
};

/**
 * Decides whether to wire up the accelerometer (`deviceorientation`) signal that
 * drives {@link resolveForceRotateDeg}.
 *
 * Pure so the gating can be unit-tested directly: a wrong condition here would
 * silently fall back to the legacy heuristic in production without failing any
 * other test. Deliberately Android-only — iOS auto-rotates and works with the
 * legacy path, so it must be excluded to keep iOS behavior unchanged.
 */
export const shouldUseTiltSignal = (args: {
  forcePortrait: boolean;
  isMobile: boolean;
  userAgent: string;
  hasDeviceOrientationApi: boolean;
}): boolean =>
  args.forcePortrait &&
  args.isMobile &&
  /android/i.test(args.userAgent) &&
  args.hasDeviceOrientationApi;
